import {
  Box,
  Center,
  Circle,
  HStack,
  Popover,
  PopoverContent,
  PopoverBody,
  useDisclosure,
  VStack,
  PopoverTrigger,
  Portal,
} from '@chakra-ui/react';
import { Button, Tooltip } from 'Atoms';
import { getModuleName, SideMenuItem } from 'containers/Navigation/pieces';
import { useMemo, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Typography } from 'Tokens';
import { NotificationIcon } from 'Tokens/Icons/Function';
import { useCurrentCompany } from 'utils/hooks';
import { useTranslation } from 'utils/translation';
import { useNotifications } from './Notifications.hooks';
import {
  EmptyNotifications,
  NotificationItem,
  ReleaseAlertNotification,
  usePendingInvitations,
} from 'Features';
import { useUserSetting } from 'containers/Navigation';
import {
  PrivacyPolicyNotification,
  privacyPolicyNotificationDismissedKey,
  privacyPolicyUpdate,
} from './Pieces/PrivacyPolicyNotification';
import { differenceInDays } from 'date-fns';
import { useAuth } from 'Providers/auth/AuthProvider';

export type NotificationsSidebarButtonType = {
  item: SideMenuItem;
  isOpen: boolean;
  isScreenLarge: boolean;
};

type SideMenuItemIconWrapperProps = {
  item: SideMenuItem;
  isOpen: boolean;
  notificationCount?: number;
};

const SideMenuItemIconWrapper = ({
  item,
  isOpen,
  notificationCount = 0,
}: SideMenuItemIconWrapperProps) => {
  if (!isOpen) {
    return (
      <Box position="relative" boxSize="20px" display="inline-block">
        <NotificationIcon boxSize="20px" color="text.sidebar" />
        {notificationCount > 0 && (
          <Circle
            size="12px"
            bg="bg.critical.accent"
            position="absolute"
            color="text.onAccent"
            fontSize="10px"
            lineHeight="100%"
            fontWeight="600"
            borderColor="bg.accent"
            zIndex="1"
            padding="2px"
            bottom="0"
            right="0"
            transform="translate(25%, 25%)"
          >
            {notificationCount > 99 ? '99+' : notificationCount}
          </Circle>
        )}
      </Box>
    );
  }
  return (
    <Center boxSize="20px">
      <item.Icon boxSize="20px" color="text.sidebar" />
    </Center>
  );
};

export const NotificationsSidebarButton = ({
  item,
  isOpen,
  isScreenLarge,
}: NotificationsSidebarButtonType) => {
  const { company } = useCurrentCompany();
  const { pathname } = useLocation();
  const { t } = useTranslation(['common']);
  const isHome = item?.path === '/';
  const isCurrent = () => (isHome ? pathname === `/${company?.id}` : pathname.includes(item.path));

  // Popover state management
  const { isOpen: isPopoverOpen, onOpen, onClose, onToggle } = useDisclosure();
  const [searchParams, setSearchParams] = useSearchParams();

  const { user } = useAuth();
  const { notifications, readNotifications, totalNotificationsCount, unreadNotificationsCount } =
    useNotifications();
  const { invitations = [] } = usePendingInvitations({ userEmail: user?.email ?? '' });
  const [isReleaseNotificationClosed] = useUserSetting('new_release_message_july', false);
  const [isPrivacyPolicyNotificationClosed, setIsPrivacyPolicyNotificationClosed] = useUserSetting(
    privacyPolicyNotificationDismissedKey,
    false
  );

  const isPrivacyPolicyNotificationTwoWeeksOld = useMemo(
    () => differenceInDays(new Date(), new Date(privacyPolicyUpdate.effectiveDate)) > 7,
    []
  );

  const notificationCount = useMemo(
    () =>
      unreadNotificationsCount +
      invitations?.length +
      (isReleaseNotificationClosed ? 0 : 1) +
      (isPrivacyPolicyNotificationTwoWeeksOld || isPrivacyPolicyNotificationClosed ? 0 : 1),
    [
      unreadNotificationsCount,
      invitations?.length,
      isReleaseNotificationClosed,
      isPrivacyPolicyNotificationTwoWeeksOld,
      isPrivacyPolicyNotificationClosed,
    ]
  );

  // Handle URL parameter for opening notifications
  useEffect(() => {
    const showNotifications = searchParams.get('showNotifications');
    if (showNotifications && !isPopoverOpen) {
      onOpen();
    }
  }, [searchParams, isPopoverOpen, onOpen]);

  // Auto-hide privacy policy notification after 7 days
  useEffect(() => {
    if (isPrivacyPolicyNotificationTwoWeeksOld && !isPrivacyPolicyNotificationClosed) {
      setIsPrivacyPolicyNotificationClosed(true);
    }
  }, [
    isPrivacyPolicyNotificationTwoWeeksOld,
    isPrivacyPolicyNotificationClosed,
    setIsPrivacyPolicyNotificationClosed,
  ]);

  // Mark notifications as read when popover is open
  useEffect(() => {
    const unreadNotificationIds =
      notifications?.filter((n) => !n.isRead).map((n) => n.id) || ([] as string[]);

    if (isPopoverOpen && unreadNotificationIds.length) {
      const timer = setTimeout(() => readNotifications(unreadNotificationIds), 2000);
      return () => clearTimeout(timer);
    }
  }, [isPopoverOpen, notifications, readNotifications]);

  useEffect(() => {
    if (!isScreenLarge && !isOpen) {
      onClose();
    }
  }, [isScreenLarge, isOpen, onClose]);

  const handleClose = () => {
    onClose();
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.delete('showNotifications');
    setSearchParams(newSearchParams.toString());
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigation
    onToggle();
  };

  return (
    <Popover
      placement="right"
      isOpen={isPopoverOpen}
      onClose={handleClose}
      closeOnBlur={true}
      isLazy={true}
    >
      <PopoverTrigger>
        <Button
          variant="unstyled"
          color="text.onAccent.disabled"
          width="100%"
          textDecoration="none"
          bg={isCurrent() ? 'bg.onAccent.selected' : 'none'}
          borderRadius="8px"
          cursor="pointer"
          _pressed={{ bg: 'bg.pressed' }}
          _hover={{ bg: 'bg.onAccent.hover', textDecoration: 'none' }}
          onClick={handleClick}
        >
          <Tooltip
            placement="right"
            label={!isOpen && isScreenLarge && getModuleName(t, item.key)}
            mt="1px"
            maxW="200px"
          >
            <HStack
              as="span"
              spacing="8px"
              p="12px"
              width="100%"
              justifyContent="space-between"
              height="36px"
            >
              <HStack as="span" spacing="8px" width="100%">
                <SideMenuItemIconWrapper
                  item={item}
                  isOpen={isOpen}
                  notificationCount={notificationCount}
                />
                <Typography
                  color="text.sidebar"
                  variant="bodyStrong"
                  transition="all 0.4s"
                  noOfLines={1}
                  opacity={isOpen ? 1 : 0}
                >
                  {getModuleName(t, item.key)}
                </Typography>
              </HStack>
              {isOpen && notificationCount > 0 && (
                <Circle
                  size="16px"
                  bg="bg.critical.accent"
                  color="text.onAccent"
                  fontSize="10px"
                  lineHeight="100%"
                  fontWeight="600"
                  borderColor="bg.accent"
                  zIndex="1"
                  padding="2px"
                >
                  {notificationCount}
                </Circle>
              )}
            </HStack>
          </Tooltip>
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          width="500px"
          height="100vh"
          border="1px solid"
          borderColor="border.decorative"
          boxShadow="0px 0px 72px -2px rgba(15, 15, 46, 0.36), 0px 6px 8px -2px rgba(15, 15, 46, 0.06), 0px 0px 0px 1px rgba(15, 15, 46, 0.12)"
          bg="bg.default"
          color="black"
        >
          <PopoverBody padding="0px">
            <VStack spacing="0px" height="100%">
              {/* Header */}
              <Box width="100%" p="16px" borderBottom="1px solid" borderColor="border.decorative">
                <Typography variant="bodyStrong">
                  {t('common:notifications.notifications')}
                </Typography>
              </Box>

              {/* Notification Content */}
              <VStack
                spacing="0px"
                width="100%"
                height="calc(100vh - 64px)"
                overflowY="auto"
                onClick={(e) => {
                  // Close popover when clicking on notification links or buttons
                  if (['A', 'BUTTON'].includes((e.target as HTMLElement).tagName)) {
                    handleClose();
                  }

                  // Check if clicked element is within a notification item
                  const checkGrandparentsForLabel = (
                    parent: HTMLElement | null,
                    label: string
                  ): boolean => {
                    if (parent?.ariaLabel === label) return true;
                    if (parent?.parentElement)
                      return checkGrandparentsForLabel(parent.parentElement, label);
                    return false;
                  };

                  if (
                    checkGrandparentsForLabel(
                      (e.target as HTMLElement).parentElement,
                      'clickable notification item'
                    )
                  ) {
                    handleClose();
                  }
                }}
              >
                {/* Release Alert */}
                {!isReleaseNotificationClosed && <ReleaseAlertNotification />}

                {/* Privacy Policy Notification */}
                {!isPrivacyPolicyNotificationClosed && <PrivacyPolicyNotification />}

                {/* Company Invitations */}
                {invitations.map((invitation) => (
                  <HStack
                    key={invitation.id}
                    width="100%"
                    justifyContent="space-between"
                    p="16px"
                    borderTop="1px solid"
                    borderColor="border.decorative"
                  >
                    <Typography variant="body">
                      {t('common:invitations.invitationNotification')}
                    </Typography>
                    <Button size="xs">{t('common:button.show')}</Button>
                  </HStack>
                ))}

                {/* Individual Notifications */}
                {notifications.map((notification) => (
                  <NotificationItem key={notification.id} notification={notification} />
                ))}

                {/* Empty State */}
                {totalNotificationsCount === 0 && isReleaseNotificationClosed && (
                  <EmptyNotifications />
                )}
              </VStack>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
